<script>
import { storeToRefs } from "pinia";
import { onMounted, ref, nextTick, onBeforeUnmount, computed } from "vue";
import { Modal, Tab } from "bootstrap";
import { onActivated } from "vue";
import { useWalletStore } from "@/stores/wallet";
import axios from "../../axios";
import { stripeFor } from "@/stripe.js";
import { useRouter } from "vue-router";
import { useSEO } from "@/composables/useSEO";
import html2pdf from "html2pdf.js";
import { useAuthStore } from "@/stores/auth";
import { useOrderStore } from "@/stores/order";
import { useTranslationStore } from "@/stores/translation";
import { useMenuStore } from "@/stores/menu";
import { useCartStore } from "@/stores/cart";
import { useAddressStore } from "@/stores/address";
import AddAddress from "@/components/AddAddress.vue";
import ReferAFriend from "@/components/ReferAFriend.vue";

export default {
  name: "ProfilePage",
  components: {
    AddAddress,
    ReferAFriend,
  },
  setup() {
    const walletStore = useWalletStore();
    const authStore = useAuthStore();
    const orderStore = useOrderStore();
    const translationStore = useTranslationStore();
    const menuStore = useMenuStore();
    const cartStore = useCartStore();

    // Translation helper
    const t = (label, modules) => {
      return translationStore.translate(label, modules);
    };

    // SEO Meta Tags with noindex (private page)
    useSEO({
      title: computed(() => t('seo_title', ['profile']) || 'Profile - Chi Hun Da Su'),
      description: computed(() => t('seo_description', ['profile']) || 'Manage your profile and orders'),
      noindex: true, // Don't index profile pages
      path: "/profile",
    });
    const addressStore = useAddressStore();

    const { balance, transactions } = storeToRefs(walletStore);
    const { orders } = storeToRefs(orderStore);
    const { addresses } = storeToRefs(addressStore);

    const { fullName, email, phoneNumber, profileImage, user } =
      storeToRefs(authStore);

    const router = useRouter();

    //Wallet Recharge
    const rechargeAmount = ref(0);
    const stripe = ref(null);
    const elements = ref(null);
    const cardElement = ref(null);
    const cardElementRef = ref(null);
    
    // Add save payment method state
    const savePaymentMethod = ref(false);
    const savedPaymentMethods = ref([]);
    const selectedSavedPaymentMethod = ref(null);
    const cardErrors = ref(null);
    const cardComplete = ref(false);
    const isProcessingPayment = ref(false);

    //Wallet Coupon functionality
    const selectedWalletCoupon = ref(null);
    const walletCouponError = ref(null);
    const walletCouponSuccess = ref(null);
    const { coupons: walletCoupons, couponsFailed: walletCouponsFailed } = storeToRefs(walletStore);

    //Password Change
    const old_password = ref(null);
    const new_password = ref(null);
    const confirm_password = ref(null);
    const password_change_error = ref(null);
    const password_change_success = ref(null);
    const profile_save_success = ref(null);
    const profile_save_error = ref(null);

    // Order details modal state
    const selectedOrder = ref(null);
    const isGeneratingPdf = ref(false);

    const openOrderDetails = (order) => {
      selectedOrder.value = order;
      // Wait for DOM update then open Bootstrap modal
      nextTick(() => {
        const modalEl = document.getElementById("orderDetailsModal");
        if (modalEl) {
          const modalInstance = Modal.getOrCreateInstance(modalEl);
          modalInstance.show();
        }
      });
    };

    const downloadOrderPdf = async () => {
      if (isGeneratingPdf.value) return;
      isGeneratingPdf.value = true;
      
      try {
        const element = document.getElementById("invoice-content");
        if (!element) {
          console.error("Invoice content element not found");
          alert("Unable to find invoice content for PDF generation.");
          return;
        }

        if (!selectedOrder.value) {
          console.error("No order selected for PDF generation");
          alert("No order selected for PDF generation.");
          return;
        }

        // Hide the modal buttons temporarily for clean PDF
        const modalFooter = element.querySelector('.modal-footer');
        const modalHeader = element.querySelector('.modal-header .btn-close');
        if (modalFooter) modalFooter.style.display = 'none';
        if (modalHeader) modalHeader.style.display = 'none';

        const options = {
          margin: [0.5, 0.5, 0.5, 0.5],
          filename: `invoice_${selectedOrder.value.order_id}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { 
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff'
          },
          jsPDF: { 
            unit: "in", 
            format: "a4", 
            orientation: "portrait" 
          }
        };

        console.log("Starting PDF generation...");
        await html2pdf().set(options).from(element).save();
        console.log("PDF generated successfully");

        // Show the modal elements again
        if (modalFooter) modalFooter.style.display = '';
        if (modalHeader) modalHeader.style.display = '';

      } catch (err) {
        console.error("Failed to generate PDF", err);
        alert("Unable to generate PDF: " + err.message + ". Please try again later.");
        
        // Ensure modal elements are visible again even if error occurs
        const element = document.getElementById("invoice-content");
        if (element) {
          const modalFooter = element.querySelector('.modal-footer');
          const modalHeader = element.querySelector('.modal-header .btn-close');
          if (modalFooter) modalFooter.style.display = '';
          if (modalHeader) modalHeader.style.display = '';
        }
      } finally {
        isGeneratingPdf.value = false;
      }
    };

    const initializeStripe = async () => {
      stripe.value = await stripeFor();
      elements.value = stripe.value.elements();
      createAndMountCardElement();
    };

    const unmountCardElement = () => {
      if (cardElement.value) {
        try {
          cardElement.value.destroy();
        } catch (error) {
          console.error('Error destroying card element:', error);
        }
        cardElement.value = null;
        cardErrors.value = null;
        cardComplete.value = false;
      }
    };

    // Order lines: legacy orders store a JSON string, weekly/staff orders a real list.
    // Package lines carry package_name/package_price instead of meal_name/price.
    const orderItems = (order) => {
      let raw = order && order.menu_item;
      if (typeof raw === 'string') { try { raw = JSON.parse(raw || '[]'); } catch (e) { raw = []; } }
      if (!Array.isArray(raw)) return [];
      return raw.map((it) => (it && it.kind === 'package'
        ? { ...it, meal_name: (String(translationStore.currentLanguage || '').startsWith('zh') && it.package_name_zh) || it.meal_name || it.package_name || 'Package', quantity: it.quantity || 1,
            selected_meal_portion_name: it.selected_meal_portion_name || (it.portions || []).map((p) => `${p.qty}× ${p.name}`).join(', '),
            selected_meal_portion_price: it.selected_meal_portion_price ?? it.total_price ?? it.package_price }
        : it));
    };
    const fetchSavedPaymentMethods = async () => {
      try {
        const response = await axios.get('/stripe-payment-methods/');
        savedPaymentMethods.value = response.data;
      } catch (error) {
        console.error('Error fetching saved payment methods:', error);
      }
    };

    const handleUseSavedPaymentMethod = (paymentMethodId) => {
      selectedSavedPaymentMethod.value = paymentMethodId;
      // Hide the card element when using saved payment method
      if (cardElement.value) {
        unmountCardElement();
      }
    };

    const handleUseNewPaymentMethod = () => {
      selectedSavedPaymentMethod.value = null;
      // Show the card element for new payment method
      createAndMountCardElement();
    };

    const createAndMountCardElement = () => {
      if (cardElement.value) return;

      cardElement.value = elements.value.create('card', {
        style: {
          base: {
            fontSize: '16px',
            color: '#32325d',
          },
        },
      });

      if (cardElementRef.value) {
        cardElement.value.mount(cardElementRef.value);
      } else {
        console.error('cardElementRef is null');
      }

      cardElement.value.on('change', (event) => {
        cardErrors.value = event.error ? event.error.message : '';
        cardComplete.value = event.complete;
      });
    };

    const rechargeValidation = () => {
      if (rechargeAmount.value > 0) {
        return false;
      }
      return true;
    };

    const handleWalletCouponApply = (coupon) => {
      selectedWalletCoupon.value = coupon;
      walletCouponSuccess.value = `Coupon ${coupon.code} applied! You'll get A$${coupon.recharge_value} for A$${coupon.recharge_amount}`;
      walletCouponError.value = null;
      rechargeAmount.value = coupon.recharge_amount;
    };

    const handleWalletCouponRemove = () => {
      selectedWalletCoupon.value = null;
      walletCouponSuccess.value = null;
      walletCouponError.value = null;
      rechargeAmount.value = 0;
    };

    const getActualRechargeAmount = () => {
      if (selectedWalletCoupon.value) {
        return selectedWalletCoupon.value.recharge_value;
      }
      return rechargeAmount.value;
    };

    const handleRechargeClick = async () => {
      if (isProcessingPayment.value) return;
      isProcessingPayment.value = true;
      try {
        const wasNewCardPayment = !selectedSavedPaymentMethod.value;
        const requestData = {
          amount: rechargeAmount.value,
        };

        // Add coupon information if selected
        if (selectedWalletCoupon.value) {
          requestData.coupon_code = selectedWalletCoupon.value.code;
          requestData.actual_amount =
            selectedWalletCoupon.value.recharge_value;
        }

        // Add save payment method flag
        requestData.save_payment_method = savePaymentMethod.value;

        const response = await axios.post(
          "/transactions/wallet/recharge/",
          requestData
        );
        const { client_secret } = response.data;

        let result;

        if (selectedSavedPaymentMethod.value) {
          // Use saved payment method
          result = await stripe.value.confirmCardPayment(client_secret, {
            payment_method: selectedSavedPaymentMethod.value,
          });
        } else {
          // Use new card
          result = await stripe.value.confirmCardPayment(client_secret, {
            payment_method: {
              card: cardElement.value,
              billing_details: {
                // Include billing details if needed
              },
            },
          });
        }

        if (result.error) {
          // Show error to customer
          console.error(result.error.message);
          alert("Payment failed: " + result.error.message);
        } else {
          if (result.paymentIntent.status === "succeeded") {
            // Payment succeeded
            alert("Payment successful!");

            // Reset coupon state immediately
            handleWalletCouponRemove();

            // Reset save payment method flag
            savePaymentMethod.value = false;

            // Reset recharge amount for non-coupon transactions
            if (!selectedWalletCoupon.value) {
              rechargeAmount.value = 0;
            }

            // Refresh saved payment methods if a new one was saved
            if (requestData.save_payment_method) {
              await fetchSavedPaymentMethods();
            }

            if (wasNewCardPayment) {
              // If a new card was used, re-initialize the card element
              if (cardElement.value) {
                unmountCardElement();
              }
              await nextTick(); // Wait for DOM to update
              createAndMountCardElement();
            }

            // Credit the wallet immediately via the confirm fallback (race-safe with the
            // Stripe webhook — the transaction row is locked) so the new balance shows right
            // away instead of the customer seeing their old balance until the webhook lands.
            try {
              await axios.post('/transactions/wallet/recharge/confirm/', { payment_intent_id: result.paymentIntent.id });
            } catch (e) { /* the webhook will still credit it */ }
            // Refresh wallet balance and transactions
            await walletStore.fetchWallet();
            await walletStore.fetchTransactions();
            
            // Navigate to wallet history tab using Bootstrap Tab API
            await nextTick(); // Ensure DOM is updated with new transaction data
            try {
              const walletHistoryTab = document.querySelector("#ewallethistory-tab");
              if (walletHistoryTab) {
                // Use Bootstrap's Tab API for reliable tab switching
                const walletHistoryTabInstance = Tab.getOrCreateInstance ? 
                  Tab.getOrCreateInstance(walletHistoryTab) :
                  new Tab(walletHistoryTab);
                walletHistoryTabInstance.show();
              }
            } catch (error) {
              console.error("Error switching to wallet history tab:", error);
              // Fallback to click method
              try {
                const walletHistoryTab = document.querySelector("#ewallethistory-tab");
                if (walletHistoryTab && walletHistoryTab.click) {
                  walletHistoryTab.click();
                }
              } catch (fallbackError) {
                console.error("Fallback tab switching also failed:", fallbackError);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error during checkout:", error.response || error);
        alert(
          "An error occurred during checkout: " +
            (error.response?.data?.detail || error.message)
        );
      } finally {
        isProcessingPayment.value = false;
      }
    };

    const passwordChangeValidation = () => {
      if (
        !old_password.value ||
        !new_password.value ||
        !confirm_password.value
      ) {
        password_change_error.value = "All fields are mandatory";
        return false;
      }
      if (new_password.value !== confirm_password.value) {
        password_change_error.value = "Confirm Password doesn't match";
        return false;
      }
      return true;
    };

    const handlePasswordChange = async () => {
      password_change_error.value = null;
      if (!passwordChangeValidation()) {
        return;
      }
      const payload = {
        old_password: old_password.value,
        new_password: new_password.value,
        confirm_password: confirm_password.value,
      };
      try {
        await authStore.handlePasswordChange(payload);
        password_change_success.value = "Password Successfully Changed";
        resetPasswordChange();
      } catch (error) {
        if (error.response && error.response.data) {
          const d = error.response.data;
          password_change_error.value = d.errors
            ? Object.values(d.errors).flat().join(' ')
            : (d.error || 'Could not change your password. Please try again.');
        } else {
          password_change_error.value =
            "An unexpected error occurred. Please try again.";
        }
      }
    };

    const resetPasswordChange = () => {
      new_password.value = null;
      old_password.value = null;
      confirm_password.value = null;
    };

    function goBack() {
      router.go(-1);
    }

    const onImageChange = (event) => {
      const file = event.target.files[0];
      if (file && file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          authStore.setProfileImage(e.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        alert("Please select a valid image file.");
      }
    };

    const handleSave = async () => {
        profile_save_success.value = "";
        profile_save_error.value = "";
      try {
        if (fullName.value) {
          await authStore.saveProfile();
          profile_save_success.value = "Profile saved successfully !!"
        } else {
          profile_save_error.value = "Full name is required";
        }
      } catch (error) {
        console.log(error);
        profile_save_error.value = toString(error);
      }
    };

    const getProfileImage = () => {
        console.log(profileImage)
        console.log(user.profile)
      if (profileImage.value) {
        return profileImage.value;
        
      } else if (user.value && user.value.profile && user.value.profile.user_image_url) {
        return user.value.profile.user_image_url;
      } else {
        return "/default-avatar.svg";
      }
    };

    const findTranslation = (meal_name) =>{
      const items = menuStore.findItem(meal_name);
      if(items.length>0){
        return t(items[0].trans_code, ['menu_item'])
      }
      else{
        return meal_name
      }
    }

    const fetchWalletData = async () => {
      await walletStore.fetchWallet();
      await walletStore.fetchTransactions();
    };

    const handleReOrder = (order) => {
      try {
        // Clear existing cart
        cartStore.resetCart();

        // Parse items from previous order
        const items = orderItems(order);

        items.forEach((itm) => {
          const cartItem = {
            id: cartStore.cart.length + 1,
            meal_name: itm.meal_name,
            meal_id: itm.meal_id || itm.id || null,
            selected_meal_portion_id: itm.selected_meal_portion_id || null,
            selected_meal_portion_name: itm.selected_meal_portion_name || "",
            selected_meal_portion_weight: itm.selected_meal_portion_weight || 0,
            selected_meal_portion_price: parseFloat(itm.selected_meal_portion_price || 0),
            selected_meal_addon_price: parseFloat(itm.selected_meal_addon_price || 0),
            addons: itm.addons || [],
            quantity: itm.quantity || 1,
            total_price: parseFloat(itm.total_price || 0),
            discount: 0,
          };
          cartStore.cart.push(cartItem);
        });

        cartStore.updateTotal();

        // Navigate to Order Now page
        router.push("/ordernow");
      } catch (error) {
        console.error("Error during reorder:", error);
        alert("Unable to reorder. Please try again.");
      }
    };

    // Check if order needs payment
    const needsPayment = (order) => {
      // Check if order has transaction and payment status requires payment
      // Exclude cancelled orders
      return order.transaction && 
             (order.transaction.status === 'requires_payment_method' || 
              order.transaction.status === 'requires_confirmation' ||
              order.transaction.status === 'requires_action') &&
             (order.status === 'ORDER_PLACED' || order.status === 'PAYMENT_FAILED') &&
             order.status !== 'CANCELLED';
    };

    // Handle payment for incomplete orders
    const handlePayment = async (order) => {
      try {
        if (!order.transaction || !order.transaction.stripe_payment_intent_id) {
          alert("Payment information not found for this order.");
          return;
        }

        const stripe = await stripeFor();
        if (!stripe) {
          alert("Payment system is not available.");
          return;
        }

        // Check if order has expired before navigating to checkout
        try {
          const response = await axios.post('/orders/check-expiry/', {
            order_id: order.id
          });

          if (response.data.status === 'cancelled') {
            // Order was expired and has been cancelled
            alert(t('order_expired_message', ['profile']) || 'Order has expired and been cancelled. Please create a new order.');
            // Refresh the order list to show updated status
            await orderStore.fetchOrders();
            return;
          } else if (response.data.status === 'no_action') {
            // Order is already processed (paid, cancelled, etc.)
            alert(t('order_already_processed', ['profile']) || 'This order has already been processed.');
            await orderStore.fetchOrders();
            return;
          }
          
          // Order is valid, proceed to checkout
          router.push({
            path: '/checkout',
            query: {
              order_id: order.id,
              payment_intent: order.transaction.stripe_payment_intent_id,
              from: 'profile'
            }
          });
        } catch (error) {
          console.error("Error checking order expiry:", error);
          // If check fails, still allow navigation to checkout
          router.push({
            path: '/checkout',
            query: {
              order_id: order.id,
              payment_intent: order.transaction.stripe_payment_intent_id,
              from: 'profile'
            }
          });
        }
      } catch (error) {
        console.error("Error handling payment:", error);
        alert("Unable to process payment. Please try again.");
      }
    };

    // Address management methods
    const openAddAddressModal = () => {
      addressStore.initializeActiveAddress();
      const modalEl = document.getElementById("addressModal");
      if (modalEl) {
        const modalInstance = Modal.getOrCreateInstance(modalEl);
        modalInstance.show();
      }
    };

    const openEditAddressModal = (address) => {
      addressStore.setActiveAddress(address);
      const modalEl = document.getElementById("addressModal");
      if (modalEl) {
        const modalInstance = Modal.getOrCreateInstance(modalEl);
        modalInstance.show();
      }
    };

    const handleDeleteAddress = async (addressId) => {
      if (confirm("Are you sure you want to delete this address?")) {
        await addressStore.deleteAddress(addressId);
      }
    };

    onMounted(async () => {
      await nextTick();
      initializeStripe();
      await fetchWalletData();
      walletStore.fetchCoupons();
      authStore.initializeProfile();
      orderStore.fetchOrders();
      await fetchSavedPaymentMethods();
      await addressStore.fetchAddresses();
    });

    // Fetch fresh wallet data whenever the component is re-activated (e.g., via <keep-alive>)
    onActivated(fetchWalletData);

    // Cleanup when component is unmounted
    onBeforeUnmount(() => {
      if (cardElement.value) {
        unmountCardElement();
      }
    });

    return {
      orderItems,
      t,
      orders,
      profileImage,
      getProfileImage,
      goBack,
      balance,
      user,
      fullName,
      handleSave,
      onImageChange,
      email,
      phoneNumber,
      transactions,
      cardElementRef,
      rechargeAmount,
      rechargeValidation,
      handleRechargeClick,
      old_password,
      new_password,
      confirm_password,
      password_change_error,
      passwordChangeValidation,
      handlePasswordChange,
      password_change_success,
      profile_save_success,
      profile_save_error,
      findTranslation,
      // Wallet coupon functionality
      walletCoupons,
      walletCouponsFailed,
      selectedWalletCoupon,
      walletCouponError,
      walletCouponSuccess,
      handleWalletCouponApply,
      handleWalletCouponRemove,
      getActualRechargeAmount,
      // Saved payment methods functionality
      savePaymentMethod,
      savedPaymentMethods,
      selectedSavedPaymentMethod,
      handleUseSavedPaymentMethod,
      handleUseNewPaymentMethod,
      cardErrors,
      cardComplete,
      isProcessingPayment,
      // Order details
      selectedOrder,
      openOrderDetails,
      downloadOrderPdf,
      handleReOrder,
      isGeneratingPdf,
      // Payment functions
      needsPayment,
      handlePayment,
      // Address management
      addresses,
      openAddAddressModal,
      openEditAddressModal,
      handleDeleteAddress,
    };
  },
};
</script>

<template>
  <div class="main-layout mb-5">
    <div class="profile-heading">
      <!-- <h2>Profile</h2> -->
    </div>
    <div class="profile-text-wrap">
      <div class="d-flex gap-3">
        <div class="profile-form w-100">
          <div class="profile-form-wrap-content">
            <div class="profile-form-heading">
              <!-- <h4 class="form-heading">Personal Detail</h4> -->

              <ul class="nav nav-tabs" id="myTab" role="tablist">
                <li class="nav-item" role="presentation">
                  <button
                    class="nav-link active"
                    id="home-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#home-tab-pane"
                    type="button"
                    role="tab"
                    aria-controls="home-tab-pane"
                    aria-selected="true"
                  >
                   {{ t('personal_details', ['profile'])}}
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button
                    class="nav-link"
                    id="ewallet-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#ewallet-tab-pane"
                    type="button"
                    role="tab"
                    aria-controls="ewallet-tab-pane"
                    aria-selected="false"
                  >
                  {{ t('wallet', ['profile'])}}
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button
                    class="nav-link"
                    id="ewallethistory-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#ewallethistory-tab-pane"
                    type="button"
                    role="tab"
                    aria-controls="ewallethistory-tab-pane"
                    aria-selected="false"
                  >
                  {{ t('wallet_history', ['profile'])}}
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button
                    class="nav-link"
                    id="orderhistory-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#orderhistory-tab-pane"
                    type="button"
                    role="tab"
                    aria-controls="profile-tab-pane"
                    aria-selected="false"
                  >
                  {{ t('order_history', ['profile'])}}
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button
                    class="nav-link"
                    id="changepassword-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#changepassword-tab-pane"
                    type="button"
                    role="tab"
                    aria-controls="changepassword-tab-pane"
                    aria-selected="false"
                  >
                  {{ t('change_password', ['profile'])}}
                  </button>
                </li>
              </ul>
            </div>

            <div class="form-input-wrap">
              <div class="tab-content" id="myTabContent">
                <div
                  class="tab-pane profile-settings fade show active"
                  id="home-tab-pane"
                  role="tabpanel"
                  aria-labelledby="home-tab"
                  tabindex="0"
                >
                  <!-- Profile Update Section -->
                  <div class="profile-update-section">
                    <div class="input-wrap">
                      <div class="row m-0">
                      <div class="col-md-12">
                        <div class="profile-img-container">
                          <div class="profile-img">
                            <!-- Dummy image shown initially -->
                            <img
                              id="profileImg"
                              :src="getProfileImage()"
                              alt="Profile Picture"
                            />
                          </div>
                          <label for="fileInput" class="edit-icon">
                            <i class="fa fa-pencil-alt"></i>
                            <!-- Pencil icon for editing -->
                          </label>
                          <input
                            type="file"
                            id="fileInput"
                            class="file-input"
                            @change="onImageChange"
                            accept="image/*"
                          />
                        </div>
                        <div>
                            <div class="alert alert-success" role="alert" v-if="profile_save_success">
                            {{ profile_save_success }}
                            </div>
                            <div class="alert alert-danger" role="alert" v-if="profile_save_error">
                            {{profile_save_error}}
                            </div>

                        </div>
                      </div>

                      <div class="col-md-6 p-2">
                        <div
                          class="input-wrap-withouticon password-bottom-space"
                        >
                          <!-- <label>Company Name</label> -->
                          <label class="form-label">{{ t('email_address', ['profile'])}}</label>
                          <input
                            type="text"
                            name="email"
                            class="form-control"
                            placeholder="Enter Email Address"
                            v-model="email"
                            disabled
                          />
                        </div>
                      </div>
                      <div class="col-md-6 p-2">
                        <div
                          class="input-wrap-withouticon password-bottom-space"
                        >
                          <!-- <label>Company Name</label> -->
                          <label
                            for="exampleFormControlInput1"
                            class="form-label"
                            >{{ t('phone_number', ['profile'])}}</label
                          >
                          <input
                            type="text"
                            name="username"
                            class="form-control"
                            placeholder="Enter Phone Number"
                            v-model="phoneNumber"
                            disabled
                          />
                        </div>
                      </div>
                    </div>
                    <div class="col-md-8 p-2">
                      <div class="input-wrap-withouticon password-bottom-space">
                        <!-- <label>Company Name</label> -->
                        <label for="exampleFormControlInput1" class="form-label"
                          >{{ t('full_name', ['profile'])}}</label
                        >
                        <input
                          type="text"
                          name="firstname"
                          class="form-control"
                          placeholder="Enter Full Name"
                          v-model="fullName"
                        />
                      </div>
                    </div>
                      <div class="footer-button p-0 pt-3">
                        <button
                          class="btn btn-secondary sm"
                          style="background-color: #1d0b00"
                          @click="goBack"
                        >
                        {{ t('back', ['profile'])}}
                        </button>
                        <a class="btn btn-primary sm" @click="handleSave">{{ t('save', ['profile'])}}</a>
                      </div>
                    </div>
                  </div>

                  <!-- Addresses Section -->
                  <div class="addresses-section mt-4">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <h4>Addresses</h4>
                      <button class="btn btn-primary" @click="openAddAddressModal">
                        <i class="fa-solid fa-plus me-2"></i>Add Address
                      </button>
                    </div>

                    <!-- No addresses message -->
                    <div v-if="!addresses || addresses.length === 0" class="text-center py-4">
                      <p class="text-muted">No address to display</p>
                    </div>

                    <!-- Address cards -->
                    <div v-else class="addresses-list">
                      <div v-for="address in addresses" :key="address.id" class="address-card mb-3">
                        <div class="card">
                          <div class="card-body position-relative">
                            <!-- Action buttons -->
                            <div class="address-actions">
                              <button 
                                class="btn btn-sm btn-outline-primary me-2" 
                                @click="openEditAddressModal(address)"
                                title="Edit Address"
                              >
                                <i class="fa-solid fa-edit"></i>
                              </button>
                              <button 
                                class="btn btn-sm btn-outline-danger" 
                                @click="handleDeleteAddress(address.id)"
                                title="Delete Address"
                              >
                                <i class="fa-solid fa-trash"></i>
                              </button>
                            </div>

                            <!-- Address content -->
                            <div class="address-content">
                              <h6 class="card-title">{{ address.name }}</h6>
                              <p class="card-text mb-1">{{ address.street_address1 }}</p>
                              <p class="card-text mb-1" v-if="address.street_address2">{{ address.street_address2 }}</p>
                              <p class="card-text mb-1">{{ address.suburbs }}, {{ address.city }}</p>
                              <p class="card-text"><strong>Postal Code:</strong> {{ address.postal_code }}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  class="tab-pane ewallet-tab fade"
                  id="ewallet-tab-pane"
                  role="tabpanel"
                  aria-labelledby="ewallet-tab"
                  tabindex="0"
                >
                  <div class="e-wallet bg-white rounded p-3 mb-0">
                    <ReferAFriend />
                    <div class="row">
                      <div class="col-lg-5 col-md-12">
                        <div class="wallet-container text-center p-3 border rounded">
                          <div class="amount-box text-center p-3">
                            <div class="wallet-icon">
                              <i class="fa-solid fa-wallet"></i>
                            </div>
                            <p>{{ t('total_balance', ['profile'])}}</p>
                            <p class="amount">A$ {{ balance }}</p>
                          </div>
                        </div>
                      </div>
                      <div class="col-lg-7 col-md-12 recharge-section">
                      
                      <!-- Coupon Success/Error Messages -->
                      <div v-if="walletCouponSuccess" class="alert alert-success alert-dismissible">
                        {{ walletCouponSuccess }}
                        <button type="button" class="btn-close" @click="handleWalletCouponRemove"></button>
                      </div>
                      <div v-if="walletCouponError" class="alert alert-danger">
                        {{ walletCouponError }}
                      </div>
                      
                      <div class="d-flex flex-column" v-if="!selectedWalletCoupon">
                        <label for="formGroupExampleInput" class="form-label"
                          >{{ t('add_amount_in_wallet', ['profile'])}}</label
                        >
                        <input
                          type="number"
                          class="form-control"
                          id="formGroupExampleInput"
                          placeholder="A$100"
                          v-model="rechargeAmount"
                        />
                      </div>
                      <div v-if="selectedWalletCoupon" class="mt-2 small text-info mb-2">
                        You will receive A${{ getActualRechargeAmount() }} in your wallet by paying A${{ selectedWalletCoupon.recharge_amount }}
                      </div>
                      
                      <!-- Saved Payment Methods -->
                      <div v-if="savedPaymentMethods.length > 0" class="saved-payment-methods mb-4">
                        <h6>{{ t('saved_payment_methods', ['profile']) || 'Saved Payment Methods' }}</h6>
                        <div class="saved-cards">
                          <div 
                            v-for="paymentMethod in savedPaymentMethods" 
                            :key="paymentMethod.id"
                            class="saved-card"
                            :class="{ active: selectedSavedPaymentMethod === paymentMethod.stripe_payment_method_id }"
                            @click="handleUseSavedPaymentMethod(paymentMethod.stripe_payment_method_id)"
                          >
                            <div class="card-info">
                              <span class="card-brand">{{ paymentMethod.card_brand.toUpperCase() }}</span>
                              <span class="card-last4">•••• {{ paymentMethod.card_last4 }}</span>
                              <span class="card-expiry">{{ paymentMethod.card_exp_month }}/{{ paymentMethod.card_exp_year }}</span>
                            </div>
                            <div class="card-actions">
                              <span v-if="paymentMethod.is_default" class="default-badge">Default</span>
                            </div>
                          </div>
                        </div>
                        
                        <div class="mt-3">
                          <button 
                            type="button" 
                            class="btn btn-outline-primary btn-sm"
                            @click="handleUseNewPaymentMethod"
                          >
                            {{ t('use_new_card', ['profile']) || 'Use New Card' }}
                          </button>
                        </div>
                      </div>
                      
                      <!-- New Card Form -->
                      <div v-if="!selectedSavedPaymentMethod" class="new-card-form">
                        <h6>{{ savedPaymentMethods.length > 0 ? (t('add_new_card', ['profile']) || 'Add New Card') : (t('enter_card_details', ['profile']) || 'Enter Card Details') }}:</h6>
                        <div id="card-element" ref="cardElementRef"></div>
                        
                        <!-- Card Error Display -->
                        <div v-if="cardErrors" class="alert alert-danger mt-2">
                          {{ cardErrors }}
                        </div>
                        
                        <!-- Save Payment Method Checkbox -->
                        <div class="form-check mt-3">
                          <input 
                            type="checkbox" 
                            class="form-check-input" 
                            id="savePaymentMethod"
                            v-model="savePaymentMethod"
                          >
                          <label class="form-check-label" for="savePaymentMethod">
                            {{ t('save_payment_method', ['profile']) || 'Save payment method for future use' }}
                          </label>
                        </div>
                        
                        <button 
                          type="button" 
                          class="mt-3 btn btn-primary"
                          :disabled="rechargeValidation() || !cardComplete || isProcessingPayment"
                          @click="handleRechargeClick"
                        >
                        {{ t('add_to_wallet', ['profile'])}}
                        </button>
                      </div>
                      
                      <!-- Use Saved Payment Method -->
                      <div v-if="selectedSavedPaymentMethod" class="use-saved-payment">
                        <button 
                          type="button" 
                          class="mt-3 btn btn-primary"
                          :disabled="rechargeValidation() || isProcessingPayment"
                          @click="handleRechargeClick"
                        >
                        {{ t('add_to_wallet', ['profile'])}}
                        </button>
                      </div>
                      </div>
                    </div>
                     <!-- Top-up offers failed to load: say so, rather than silently
                          showing no offers and letting the customer pay without their bonus. -->
                      <div class="mt-4 alert alert-warning py-2 px-3" v-if="walletCouponsFailed">
                        <i class="fa fa-triangle-exclamation me-1"></i>
                        We couldn't load the current top-up offers. Please refresh before topping up so your bonus is applied.
                      </div>
                     <!-- Wallet Coupons Section -->
                      <div class="mt-4" v-if="walletCoupons && walletCoupons.length > 0">
                        <label class="form-label">{{ t('available_wallet_coupons', ['profile'])}}</label>
                        <div class="coupon-scroll-container">
                          <div class="coupon-grid">
                            <div class="coupon-card-wrapper" v-for="coupon in walletCoupons" :key="coupon.id">
                              <div class="card coupon-card" :class="{ 'coupon-applied': selectedWalletCoupon && selectedWalletCoupon.id === coupon.id }" @click="handleWalletCouponApply(coupon)">
                                <div class="card-body p-2 text-center">
                                  <div class="coupon-amounts">
                                    <span class="pay-amount">Pay A${{ coupon.recharge_amount }}</span>
                                    <i class="fa fa-arrow-right mx-1"></i>
                                    <span class="get-amount">Get A${{ coupon.recharge_value }}</span>
                                  </div>
                                  <button 
                                    v-if="!(selectedWalletCoupon && selectedWalletCoupon.id === coupon.id)"
                                    class="btn btn-sm coupon-apply-btn btn-outline-primary mt-2"
                                  >
                                    Apply
                                  </button>
                                  <div v-if="selectedWalletCoupon && selectedWalletCoupon.id === coupon.id" class="applied-badge mt-2">
                                    <i class="fa fa-check-circle"></i> Applied
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                  </div>
                </div>
                <div
                  class="tab-pane ewallethistory-tab fade"
                  id="ewallethistory-tab-pane"
                  role="tabpanel"
                  aria-labelledby="ewallethistory-tab"
                  tabindex="0"
                >
                  <div class="e-wallet bg-white rounded mb-3">
                    <div class="wallet-container text-center">
                      <div class="txn-history">
                        <div class="wallet-section">
                          <!-- Wallet History Section -->
                          <div class="section wallet-history">
                            <h2>{{ t('wallet_history', ['profile'])}}</h2>
                            <p class="latest-transactions-text">{{ t('latest_10_transactions', ['profile']) || 'Latest 10 Transactions' }}</p>
                            <!-- Wallet History -->
                            <template v-if="transactions && transactions.length > 0">
                              <div
                                v-for="txn in transactions"
                                :key="txn.id"
                                class="transaction"
                                :class="
                                  txn.order_type === 'WALLET_RECHARGE'
                                    ? 'credit'
                                    : 'debit'
                                "
                              >
                                
                                <div class="left">
                                  <p>
                                    <span>{{ t('transaction_id', ['profile']) }}</span>
                                    {{ txn.transaction_id }}
                                  </p>
                                  <p>
                                    <span>{{ t('operation_type', ['profile']) }}</span>
                                    {{ t(txn.order_type, ['profile']) }}
                                  </p>
                                  <p :class="txn.status === 'FAILED' ? 'text-danger' : 'text-success'">
                                    <span>{{ t('payment_status', ['profile']) }}</span> {{ t(txn.status, ['profile']) }}
                                  </p>
                                  <p>
                                    <span>{{ t('date_time', ['profile']) }}</span> {{txn.time}}
                                  </p>
                                   <p v-if="txn.order_type === 'WALLET_RECHARGE' && txn.coupon_code">
                                    <span>{{ t('amount_paid', ['profile']) }}</span> A${{ txn.amount }}
                                  </p>
                                </div>
                                <div class="d-flex flex-column align-items-end">
                                <div class="amount">
                                  {{
                                    txn.order_type === "WALLET_RECHARGE"
                                      ? "+"
                                      : "-"
                                  }}
                                  A${{ txn.actual_credit_amount || txn.amount }}
                                </div>
                                <span v-if="txn.coupon_code" class="coupon-tag">
                                  <i class="fa fa-ticket-alt me-1"></i>{{ t('Applied', ['profile']) }} {{ txn.coupon_code }}
                                </span>
                                </div>
                              </div>
                            </template>
                            <p class="txn-list" v-else>
                              {{t('no_transaction_to_display',['profile'])}}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  class="tab-pane orderhistory-settings fade"
                  id="orderhistory-tab-pane"
                  role="tabpanel"
                  aria-labelledby="orderhistory-tab"
                  tabindex="0"
                >
                  <div
                    class="store-n-diliveri order-history bg-white rounded mb-3"
                  >
                    <div class="order-history">
                      <!-- Order Card 1 -->
                      <template v-if="orders && orders.length > 0">
                        <div class="order-card" v-for="order in orders" :key="order.id" >
                          <div class="order-header">
                            <div class="delivery">{{t(order.order_type, ['profile'])}}</div>
                            <div class="d-flex justify-content-start">
                             <div v-if="order.coupon" class="coupon-applied-badge mx-1">
                               {{ t('coupon_applied', ['profile']) || 'Coupon Applied' }}
                             </div>
                             <div class="status" :class="`status-${order.status.toLowerCase()}`">{{ t(order.status, ['profile']) }}</div>
                           </div>              
                          </div>
                          <div class="order-details">
                            <strong>{{ t('order_id', ['profile']) }}:</strong> {{ order.order_id}} <br />
                            <strong>{{ t('date_time', ['profile']) }}:</strong> {{order.order_time}}
                          </div>
                          <div class="order-item" v-for="(item, i) in orderItems(order)" :key="item.id || i">
                            <img
                              src="https://img.icons8.com/color/48/000000/checked--v1.png"
                              alt="Item Icon"
                            />
                            <span>{{findTranslation(item.meal_name)}}</span>
                          </div>
                          <!-- Payment Timeout Warning for Unpaid Orders -->
                          <div v-if="needsPayment(order)" class="payment-timeout-warning mb-2">
                            <div class="alert alert-success py-1 px-2 mb-2">
                              <i class="fa fa-clock me-1"></i>
                              <small>{{ t('payment_timeout_warning', ['profile']) }}</small>
                            </div>
                          </div>
                          
                          <div class="order-footer">
                            <div>
                              <div class="price">A$ {{ order.total_price}}</div>
                              <h6 v-if="order.coupon" class="text-danger">
                                {{ t('you_saved', ['profile']) }} A${{ order.discount }}
                              </h6>
                            </div>
                            <div>
                              <button class="orderdetails-btn mx-2" @click="openOrderDetails(order)">Details</button>
                              <button v-if="needsPayment(order)" class="payment-btn mx-2" @click="handlePayment(order)">Complete Payment</button>
                              <button class="reorder-btn" @click="handleReOrder(order)">Reorder</button>
                            </div>
                          </div>
                        </div>
                      </template>
                      <div v-else class="text-center p-5">
                        <p>{{ t('no_order_history_to_display', ['profile']) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  class="tab-pane changepassword-settings fade"
                  id="changepassword-tab-pane"
                  role="tabpanel"
                  aria-labelledby="changepassword-tab"
                  tabindex="0"
                >
                  <div class="row">
                    <div
                      class="alert alert-danger"
                      role="alert"
                      v-if="password_change_error"
                    >
                      {{ password_change_error }}
                    </div>
                    <div
                      class="alert alert-success"
                      role="alert"
                      v-if="password_change_success"
                    >
                      {{ password_change_success }}
                    </div>
                    <div class="col-md-12 p-2">
                      <div class="input-wrap-withouticon password-bottom-space">
                        <label for="oldpassword" class="form-label"
                          >{{ t('old_password', ['profile'])}}</label
                        >
                        <input
                          id="oldpassword"
                          type="password"
                          name="old_password"
                          v-model="old_password"
                          class="form-control"
                          placeholder="Enter old password"
                        />
                      </div>
                    </div>

                    <div class="col-md-12 p-2">
                      <div class="input-wrap-withouticon password-bottom-space">
                        <label for="newpassword" class="form-label"
                          >{{ t('new_password', ['profile'])}}</label
                        >
                        <input
                          id="newpassword"
                          type="password"
                          name="new_password"
                          v-model="new_password"
                          class="form-control"
                          placeholder="Enter new password"
                        />
                      </div>
                    </div>

                    <div class="col-md-12 p-2">
                      <div class="input-wrap-withouticon password-bottom-space">
                        <!-- <label>Company Name</label> -->
                        <label for="exampleFormControlInput1" class="form-label"
                          >{{ t('confirm_password', ['profile'])}}</label
                        >
                        <input
                          type="password"
                          name="confirm_password"
                          v-model="confirm_password"
                          class="form-control"
                          placeholder="Enter new password again"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="footer-button">
                    <a class="btn btn-primary sm" @click="handlePasswordChange"
                      >{{ t('change_password', ['profile'])}}</a
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
<!-- Order Details Modal -->
<div class="modal fade" id="orderDetailsModal" tabindex="-1" aria-hidden="true">
  <div class="modal-dialog modal-dialog-scrollable modal-lg">
    <div class="modal-content" id="invoice-content">
      <div class="modal-header">
        <h5 class="modal-title">{{ t('order_details', ['profile']) || 'Order Details' }}</h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body" v-if="selectedOrder">
        <div class="mb-3">
          <p><strong>{{ t('order_id', ['profile']) }}:</strong> {{ selectedOrder.order_id }}</p>
          <p><strong>{{ t('date_time', ['profile']) }}:</strong> {{ selectedOrder.order_time }}</p>
          <p><strong>{{ t('status', ['profile']) }}:</strong> <span class="status-badge" :class="`status-${selectedOrder.status.toLowerCase()}`">{{ t(selectedOrder.status, ['profile']) }}</span></p>
          <!-- Added more order information -->
          <p><strong>{{ t('order_type', ['profile']) || 'Order Type' }}:</strong> {{ t(selectedOrder.order_type, ['profile']) }}</p>
          <p v-if="selectedOrder.order_type === 'DELIVERY'"><strong>{{ t('delivery_location', ['profile']) || 'Delivery Location' }}:</strong> {{ selectedOrder.delivery_location }}</p>
          <p v-else><strong>{{ t('pickup_location', ['profile']) || 'Pickup Location' }}:</strong> {{ selectedOrder.pickup_location }}</p>
          <p v-if="selectedOrder.time_slot"><strong>{{ t('time_slot', ['profile']) || 'Time Slot' }}:</strong> {{ selectedOrder.time_slot }}</p>
          <p v-if="selectedOrder.notes"><strong>{{ t('notes', ['profile']) || 'Notes' }}:</strong> {{ selectedOrder.notes }}</p>
        </div>
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>{{ t('item', ['profile']) || 'Item' }}</th>
              <th class="text-end">{{ t('qty', ['profile']) || 'Qty' }}</th>
              <th class="text-end">{{ t('price', ['profile']) || 'Price' }}</th>
              <th class="text-end">{{ t('total', ['profile']) || 'Total' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in orderItems(selectedOrder)" :key="item.id || i">
              <td>
                {{ findTranslation(item.meal_name) }}
                <br v-if="item.addons && item.addons.length > 0">
                <small v-if="item.addons && item.addons.length > 0" class="text-muted">
                 <strong>Addons:</strong> {{ item.addons.map(addon => addon.name).join(', ') }}
                </small>
              </td>
              <td class="text-end">{{ item.quantity || 1 }}</td>
              <td class="text-end">A$ {{ ((item.selected_meal_portion_price || 0) + (item.selected_meal_addon_price || 0)).toFixed(2) }}</td>
              <td class="text-end">A$ {{ (item.total_price || ((item.selected_meal_portion_price || 0) + (item.selected_meal_addon_price || 0)) * (item.quantity || 1)).toFixed(2) }}</td>
            </tr>
          </tbody>
        </table>
        <div class="text-end">
                     <small class="text-danger" v-if="selectedOrder.discount && selectedOrder.discount > 0">{{ t('you_saved', ['profile']) || 'Total Discount' }}: A$ {{ selectedOrder.discount }}</small>
          <h5>{{ t('total', ['profile']) || 'Total' }}: A$ {{ selectedOrder.total_price }}</h5>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-primary" @click="downloadOrderPdf" :disabled="isGeneratingPdf">
          <span v-if="isGeneratingPdf" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          {{ isGeneratingPdf ? 'Generating PDF...' : (t('download_pdf', ['profile']) || 'Download PDF') }}
        </button>
        <button type="button" class="btn btn-outline-danger" data-bs-dismiss="modal">{{ t('close', ['profile']) || 'Close' }}</button>
      </div>
    </div>
  </div>
</div>

<!-- Add Address Modal -->
<AddAddress />
</template>

<style>
/* -----------profile-------------- */

/* Wallet Coupon Improvements */
.coupon-scroll-container {
  max-height: 300px;
  overflow-y: auto;
  padding-right: 5px;
}

.coupon-scroll-container::-webkit-scrollbar {
  width: 6px;
}

.coupon-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.coupon-scroll-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.coupon-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.coupon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  padding: 2px;
}

.coupon-card-wrapper {
  min-width: 0; /* Prevent overflow in grid */
}

.coupon-card {
  border: 2px solid #e9ecef;
  border-radius: 12px;
  transition: all 0.3s ease;
  height: 100%;
  cursor: pointer;
  position: relative;
}

.coupon-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0,0,0,0.15);
  border-color: #944c22;
}

.coupon-card.coupon-applied {
  border-color: #28a745;
  background-color: #f8fff9;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.2);
}

.coupon-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.coupon-card .card-title {
  color: #944c22;
  font-weight: bold;
  font-size: 0.9rem;
  margin: 0;
  font-family: 'Courier New', monospace;
}

.applied-badge {
  color: #28a745;
  font-size: 0.9rem;
}

.coupon-name {
  color: #666;
  font-size: 0.8rem;
  margin: 0 0 8px 0;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.coupon-amounts {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px 0;
  font-size: 0.85rem;
  font-weight: 600;
  flex-wrap: wrap;
}

.pay-amount {
  color: #dc3545;
  background-color: #fff5f5;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #f5c6cb;
}

.get-amount {
  color: #28a745;
  background-color: #f8fff9;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #c3e6cb;
}

.coupon-apply-btn {
  width: 80%;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.coupon-apply-btn:not(:disabled):hover {
  transform: scale(1.02);
}

.coupon-apply-btn.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

/* Improved Wallet Layout */
.recharge-section {
  padding: 15px;
}

@media screen and (max-width: 991px) {
  .recharge-section {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #e9ecef;
  }
}

@media screen and (min-width: 992px) {
  .recharge-section {
    padding-left: 25px;
    border-left: 1px solid #e9ecef;
  }
}

.wallet-history h2 {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
}

.latest-transactions-text {
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
  font-style: italic;
}

.transaction {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 15px;
  background-color: #f4f8fb;
  position: relative;
  overflow: hidden;
}
.coupon-applied-badge {
    top: 0;
    right: 0;
    background-color: #28a745;
    color: white;
    padding: 3px 10px;
    font-size: 11px;
    font-weight: 600;
    border-radius: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
.transaction .left {
  flex: 1;
}
.transaction .left p {
  margin: 5px 0;
  font-size: 14px;
  color: #555;
}
.transaction .left span {
  font-weight: bold;
  color: #333;
}
.transaction .payment-platform {
  font-size: 14px;
  font-weight: bold;
  padding: 6px 12px;
  border-radius: 4px;
  background-color: #007bff;
  color: white;
}
.transaction .amount {
  font-size: 16px;
  font-weight: bold;
}
.transaction.credit .amount {
  color: #28a745;
}
.transaction.debit .amount {
  color: #dc3545;
}

.coupon-tag {
  background-color: #e2f3e8;
  color: #28a745;
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 12px;
  margin-top: 5px;
  font-weight: 500;
}

.order-details-container {
  max-width: 600px;
  margin: 20px auto;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.header {
  display: flex;
  justify-content: space-between;
  padding: 15px;
  align-items: center;
  border-bottom: 1px solid #ddd;
}
.header .title {
  font-size: 18px;
  font-weight: bold;
}
.header .help {
  color: #007bff;
  font-size: 14px;
  cursor: pointer;
}
.address-section {
  padding: 15px;
  border-bottom: 1px solid #ddd;
}
.address-section h3 {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
  color: #333;
}
.address-section p {
  margin: 5px 0;
  font-size: 14px;
  color: #555;
}
.status-section {
  display: flex;
  align-items: center;
  padding: 15px;
  background-color: #f4f8fb;
  border-bottom: 1px solid #ddd;
}
.status-section img {
  width: 50px;
  height: 50px;
  margin-right: 10px;
}
.status-section .status-text {
  font-weight: bold;
  color: #333;
}
.status-section .status-date {
  font-size: 14px;
  color: #555;
}
.order-summary {
  padding: 15px;
}
.order-summary h3 {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
}
.order-summary .summary-item {
  display: flex;
  justify-content: space-between;
  margin: 8px 0;
  font-size: 14px;
}
.order-summary .summary-item .bold {
  font-weight: bold;
}
.total {
  font-size: 18px;
  font-weight: bold;
  margin-top: 15px;
  text-align: right;
}

.order-history {
  max-width: 600px;
  margin: 20px auto;
}
.order-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 15px;
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  position: relative;
}
.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.order-header .delivery {
  font-weight: bold;
  font-size: 16px;
}
/* Base status styling with default color */
.order-header .status,
.status-badge {
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 15px;
  font-weight: bold;
  text-transform: uppercase;
  display: inline-block;
  /* Default color for any undefined status */
  background-color: #808080;
  color: #FFFFFF;
}

/* Dynamic status colors based on order status - Higher specificity */
.order-header .status.status-order_placed,
.status-badge.status-order_placed {
  background-color: #ADD8E6 !important;
  color: #2F4F4F !important;
}

.order-header .status.status-payment_success,
.status-badge.status-payment_success {
  background-color: #32CD32 !important;
  color: #FFFFFF !important;
}

.order-header .status.status-payment_failed,
.status-badge.status-payment_failed {
  background-color: #FF6347 !important;
  color: #FFFFFF !important;
}

.order-header .status.status-ready_for_pickup,
.status-badge.status-ready_for_pickup {
  background-color: #FFD700 !important;
  color: #8B4513 !important;
}

.order-header .status.status-out_for_delivery,
.status-badge.status-out_for_delivery {
  background-color: #87CEEB !important;
  color: #191970 !important;
}

.order-header .status.status-delivered,
.status-badge.status-delivered {
  background-color: #228B22 !important;
  color: #FFFFFF !important;
}

.order-header .status.status-cancelled,
.status-badge.status-cancelled {
  background-color: #808080 !important;
  color: #FFFFFF !important;
}
.order-details {
  margin-bottom: 10px;
  font-size: 14px;
  color: #666;
}
.order-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}
.order-item img {
  width: 16px;
  height: 16px;
  margin-right: 8px;
}
.order-item span {
  font-weight: bold;
  font-size: 14px;
  color: #333;
}
.order-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.order-footer > div:last-child {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  min-width: 0;
}
.price {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}
.reorder-btn, .orderdetails-btn, .payment-btn {
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: auto;
}

.reorder-btn {
  background-color: #944c22;
  color: #fff;
}
.reorder-btn:hover {
  background-color: #7a3d1c;
}

.orderdetails-btn {
  background-color: #944c22;
  color: #fff;
}
.orderdetails-btn:hover {
  background-color: #0D6EFD;
}

.payment-btn {
  background-color: #28a745;
  color: #fff;
}
.payment-btn:hover {
  background-color: #218838;
}

/* Payment timeout warning styles for order list */
.payment-timeout-warning .alert-success {
  background-color: #d1e7dd;
  border-color: #badbcc;
  color: #0f5132;
  font-size: 12px;
  border-radius: 4px;
}

.payment-timeout-warning .alert-success i {
  color: #198754;
}

/* 移动端优化 */
@media screen and (max-width: 768px) {
  .order-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .order-footer > div:last-child {
    width: 100%;
    justify-content: space-between;
  }
  
  .reorder-btn, .orderdetails-btn, .payment-btn {
    flex: 1;
    text-align: center;
    min-width: 80px;
    font-size: 11px;
    padding: 8px 4px;
  }
  
  /* Mobile responsive for payment timeout warning in order list */
  .payment-timeout-warning .alert-success {
    font-size: 10px;
    padding: 4px 8px;
    margin-bottom: 8px !important;
  }
  
  .payment-timeout-warning .alert-success small {
    font-size: 10px;
    line-height: 1.3;
    word-break: break-word;
  }
}

.profile-heading {
  margin: 16px 88px;
  /* background: linear-gradient(9deg, rgb(197 93 33) 0%, rgba(137, 65, 24, 1) 55%); */
  background-image: linear-gradient(
      45deg,
      rgba(22, 22, 22, 0.5),
      rgba(33, 38, 43, 0.5)
    ),
    url("@/assets/about-banner.png");
  background-size: cover;
  border-radius: 8px;
  padding: 16px;
  height: 200px;
  position: relative;
  padding-left: 30px;
  display: flex;
}

.billing-content-wrap {
  min-height: 70vh;
}

.profile-heading::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: repeating-linear-gradient(
    328deg,
    rgba(255, 255, 255, 0.06),
    rgba(255, 255, 255, 0.06) 20px,
    rgba(255, 255, 255, 0) 20px,
    rgba(255, 255, 255, 0) 40px
  );
  background-size: 200% 100%;
  opacity: 0.3;
  pointer-events: none;
  background-position: left;
}

.profile-heading h2 {
  color: #fff;
  font-weight: 400 !important;
  margin-top: 8px;
  font-size: 30px !important;
}

.profile-text-wrap {
  background-color: #ffffff;
  /* padding: 16px; */
  margin: -174px 118px 0;
  position: relative;
  border-radius: 8px;
}

.profile-settings {
  min-height: 50vh;
}

.profile-side-bar {
  width: 250px;
  border: 1px solid #ffe0ce;
  /* height: calc(100vh - 143px); */
  padding: 16px 0;
  border-radius: 8px;
  overflow-y: auto;
}

.profile-form {
  width: calc(100% - 250px);
  border: 1px solid #d2d2d2;
  border-radius: 8px;
  /* height: calc(100vh - 143px); */
  overflow: hidden;
}

.profile-side-bar ul {
  display: flex;
  align-items: left;
  flex-direction: column;
  height: auto;
  list-style-type: none;
  gap: 15px;
  margin-bottom: 30px;
}

.profile-side-bar ul li {
  padding: 0 16px;
}

.profile-side-bar a.active {
  background: linear-gradient(9deg, rgb(197 93 33) 0%, #894118 55%);
  color: #fff;
  border-radius: 6px;
}

.profile-side-bar ul a {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  padding: 12px 16px;
  color: #894118;
}

.profile-form-heading {
  padding-top: 16px;
  border-bottom: 1px solid #ffe0ce;
}

.profile-form-wrap-content .form-input-wrap {
  min-height: 50vh;
}

.form-input-wrap {
  padding: 16px;
  overflow-y: auto;
  /* height: calc(100vh - 145px); */
}

.footer-button {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px;
  border-top: 1px solid #ffe0ce;
  /* position: sticky; */
  /* bottom: 0; */
  width: 100%;
  gap: 8px;
  background-color: #fff;
}

.profile-img-container {
  position: relative;
  width: 200px;
  height: 147px;
  text-align: center;
}

/* Profile image styles */
.profile-img {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  overflow: hidden;
  border: 5px solid white;
}

.profile-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Edit icon */
.edit-icon {
  position: absolute;
  top: 9px;
  right: 60px;
  background-color: rgb(194 92 33);
  color: white;
  padding: 5px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 15px;
  transition: background-color 0.3s ease;
  width: 32px;
  height: 32px;
}

.file-input {
  display: none;
  /* Hide the file input */
}

.billing-header {
  width: 100%;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 9;
}

.billing-content-wrap {
  overflow: auto;
  height: 100%;
}

.sign-up-wrap {
  padding: 16px !important;
  background-color: #fff;
  position: sticky;
  bottom: 0;
  margin-top: 8px !important;
}

.profile-form-heading .nav-tabs .nav-link {
  color: var(----card-location-color) !important;
}

.profile-form-heading .nav-tabs .nav-link.active {
  color: white !important;
  background-color: var(--dark-brown-color);
}

.order-history {
  /* padding: 0.75rem; */
}

.order-history .Item-for-checkout {
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid #d2d2d2;
  margin-bottom: 10px;
}

.order-history .order-item p {
  color: var(--card-location-color);
}

.order-history .Item-for-checkout .price-n-item-option {
  flex-direction: column;
  justify-content: flex-end;
  gap: 0;
}

.meal-addons {
  gap: 10px;
  width: 100%;
}

.meal-addons .btn-check:checked + .btn {
  background-color: var(--light-brown-color);
  border: 2px solid var(--dark-brown-color);
  color: var(--card-heading-color);
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.meal-addons .btn-check + .btn {
  background-color: white;
  border: 1px solid var(--card-location-color);
  color: var(--card-heading-color);
  width: 100%;
  justify-content: space-between;
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  padding: 1rem;
}

.price-n-item-option {
  text-align: left;
}

.price-n-item-option .payment-type-history {
  padding-top: 0.5rem;
  bottom: 1px solid #d2d2d2;
  width: 100%;
}

.price-n-item-option .payment-type-history p {
  color: var(--card-location-color);
  font-size: 12px;
  text-align: left;
}

.price-n-item-option .payment-type-history .payment-portal {
  font-size: 12px;
  color: var(--order-background-color);
  font-weight: 700;
}

.price-n-item-option .overall-price {
  color: var(--order-background-color);
}

/* ------------------------- e-wallet -------------------- */

.wallet-icon i {
  font-size: 48px;
  color: var(--card-location-color);
}

.wallet-container {
  /* background: #f1f5f0; */
}

.page-title {
  text-align: left;
}

.fa-user {
  float: right;
}

.fa-align-left {
  margin-right: 15px;
}

.amount-box img {
  width: 50px;
}

.amount {
  font-size: 35px;
}

.amount-box p {
  margin-top: 10px;
  margin-bottom: -10px;
}

.btn-group {
  margin: 20px auto;
  max-width: 150px;
}

.btn-group .btn {
  /* margin: 8px; */
  /* border-radius: 20px !important; */
  font-size: 12px;
}

.txn-history {
  text-align: left;
}

.txn-list {
  background-color: #fff;
  padding: 12px 10px;
  color: #777;
  font-size: 14px;
  margin: 7px 0;
}

.debit-amount {
  color: red;
  float: right;
}

.credit-amount {
  color: green;
  float: right;
}

.footer-menu {
  margin: 20px -20px 0;
  bottom: 0;
  border-top: 1px solid #ccc;
  padding: 10px 10px 0;
}

.footer-menu p {
  font-size: 12px;
}

@media screen and (max-width: 800px) {
  .wallet-container {
    height: 115%;
    bottom: 20px;
    /* margin-top: 100px; */
  }
}

@media screen and (max-width: 768px) {
  .profile-heading {
    margin: 0;
  }

  .profile-text-wrap {
    margin-left: 10px;
    margin-right: 10px;
  }

  .nav-link.active,
  .nav-link:hover {
    color: var(--dark-brown-color) !important;
  }

  .order-history .Item-for-checkout {
    flex-direction: column;
    row-gap: 1rem;
  }

  .order-history .Item-for-checkout .order-item {
    width: 100%;
  }

  .order-history .Item-for-checkout .price-n-item-option {
    width: 100%;
    border-top: 1px dashed var(--card-location-color);
    padding-top: 1rem;
  }

  .order-history .Item-for-checkout .price-n-item-option p {
    width: 100%;
    text-align: start;
  }
}

@media screen and (max-width: 575px) {
  .profile-form-heading {
    padding: 10px;
    border: none;
  }

  .profile-form-heading .nav-tabs {
    display: flex;
    flex-direction: column;
  }

  .profile-form-heading .nav-tabs .nav-item .nav-link {
    width: 100%;
    border-radius: 0.5rem;
    margin-bottom: 0.25rem;
  }

  .profile-form-heading .nav-tabs .nav-item .nav-link:hover {
    width: 100%;
    color: white !important;
  }

  /* Mobile Coupon Responsive Styles */
  .coupon-scroll-container {
    max-height: 250px;
  }

  .coupon-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .coupon-amounts {
    font-size: 0.75rem;
    flex-direction: column;
    gap: 4px;
  }

  .coupon-amounts i {
    transform: rotate(90deg);
    margin: 2px 0;
  }

  .pay-amount, .get-amount {
    padding: 4px 8px;
    font-size: 0.75rem;
  }

  .e-wallet .col-md-6 {
    padding: 10px !important;
  }
}

@media screen and (min-width: 576px) and (max-width: 768px) {
  /* Tablet Coupon Styles */
  .coupon-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
  
  .coupon-scroll-container {
    max-height: 280px;
  }
}

/* Saved Payment Methods Styles */
.saved-payment-methods {
  margin-bottom: 1rem;
}

.saved-cards {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.saved-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 2px solid #e4e4e4;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: white;
}

.saved-card:hover {
  border-color: var(--order-background-color);
}

.saved-card.active {
  border-color: var(--order-background-color);
  background-color: #f8f9fa;
}

.card-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.card-brand {
  font-weight: 600;
  color: var(--dark-brown-color);
  font-size: 0.9rem;
}

.card-last4 {
  color: var(--card-heading-color);
  font-family: monospace;
  font-size: 1rem;
}

.card-expiry {
  color: var(--card-location-color);
  font-size: 0.8rem;
}

.card-actions {
  display: flex;
  align-items: center;
}

.default-badge {
  background-color: var(--order-background-color);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.new-card-form {
  padding: 1rem;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  background-color: #f8f9fa;
}

.form-check {
  margin-top: 1rem;
}

.form-check-input:checked {
  background-color: var(--order-background-color);
  border-color: var(--order-background-color);
}

.form-check-label {
  font-size: 0.9rem;
  color: var(--card-heading-color);
}

/* PDF Export Styles */
#invoice-content {
  background: white !important;
}

#invoice-content .modal-header {
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 1rem;
}

#invoice-content .modal-body {
  color: #333 !important;
}

#invoice-content .table {
  border-collapse: collapse;
}

#invoice-content .table th,
#invoice-content .table td {
  border: 1px solid #dee2e6 !important;
  padding: 8px !important;
}

#invoice-content .table thead th {
  background-color: #f8f9fa !important;
  font-weight: bold;
}

@media print {
  #invoice-content .modal-footer,
  #invoice-content .btn-close {
    display: none !important;
  }
}

/* Address Section Styles */
.addresses-section {
  border-top: 1px solid #e9ecef;
  padding-top: 1.5rem;
}

.addresses-section h4 {
  color: var(--dark-brown-color);
  font-weight: 600;
}

.address-card .card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.address-card .card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-color: var(--order-background-color);
}

.address-card .card-body {
  padding: 1.25rem;
}

.address-actions {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
}

.address-content {
  padding-right: 100px; /* Space for action buttons */
}

.address-content .card-title {
  color: var(--dark-brown-color);
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.address-content .card-text {
  color: var(--card-location-color);
  font-size: 0.9rem;
  line-height: 1.4;
}

.profile-update-section {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
}

@media screen and (max-width: 768px) {
  .address-actions {
    position: static;
    margin-bottom: 1rem;
    justify-content: flex-end;
  }
  
  .address-content {
    padding-right: 0;
  }
  
  .d-flex.justify-content-between {
    flex-direction: column;
    gap: 1rem;
  }
  
  .d-flex.justify-content-between button {
    align-self: flex-end;
  }
}
</style>
